import type { RuntimeLimits } from './types';

/** Fallback for seeding the Runtime form before the daemon's config arrives (it always sends real values). */
export const RUNTIME_LIMIT_DEFAULTS: RuntimeLimits = {
  localShellTimeoutMs: 30000, codeModeDefaultYieldTimeMs: 10000, codeModeMaxOutputTokens: 10000,
  memorySemanticFloorPerMille: 200,
  memoryDuplicatePerMille: 930, memoryParaphrasePerMille: 700,
  memoryImportanceWeightPerMille: 100, memoryVitalityWeightPerMille: 100, memoryCuratorMaxOps: 2,
  // The two automatic memory-maintenance gates, mirroring `src/store/config/limits.ts`: one day and five
  // OTHER conversations since the last consolidation.
  memoryDreamingMinHours: 24, memoryDreamingMinSessions: 5,
  skillReviewMaxOps: 1, skillReviewMaxCorrections: 2,
  skillReviewEveryToolCalls: 100, skillReviewMinToolCalls: 1,
  toolDeferThreshold: 10, stepContextEveryToolCalls: 20, planModeExploreAgents: 3, planModePlanAgents: 3, eventRetentionDays: 30,
  providerRequestRetentionDays: 14, providerRequestRetentionMiB: 1024, originIpRetentionDays: 30,
  streamSilenceLimitMs: 75000, streamReviveSilenceLimitMs: 45000, toastDurationMs: 4500,
};

/** Browser mirror of the daemon's runtime clamp table. The web cannot import from `src/`, so the text
 * parity test compares every entry and Tool loading consumes this same table instead of restating its
 * threshold attributes. */
export const RUNTIME_LIMIT_BOUNDS: Record<keyof RuntimeLimits, readonly [min: number, max: number]> = {
  localShellTimeoutMs: [10_000, 300_000],
  codeModeDefaultYieldTimeMs: [10_000, 600_000],
  codeModeMaxOutputTokens: [1_000, 50_000],
  memorySemanticFloorPerMille: [100, 800],
  memoryDuplicatePerMille: [500, 980],
  memoryParaphrasePerMille: [500, 980],
  memoryImportanceWeightPerMille: [0, 300],
  memoryVitalityWeightPerMille: [0, 300],
  memoryCuratorMaxOps: [0, 6],
  // Both dreaming gates mirror the daemon's clamp: one hour is the stale-lock window and a week the useful
  // ceiling for "since the last consolidation"; past fifty conversations the gate stops being a schedule.
  memoryDreamingMinHours: [1, 168],
  memoryDreamingMinSessions: [1, 50],
  skillReviewMaxOps: [0, 6],
  skillReviewMaxCorrections: [0, 5],
  skillReviewEveryToolCalls: [1, 200],
  skillReviewMinToolCalls: [1, 200],
  toolDeferThreshold: [1, 100],
  stepContextEveryToolCalls: [10, 100],
  planModeExploreAgents: [0, 5],
  planModePlanAgents: [0, 5],
  eventRetentionDays: [1, 365],
  providerRequestRetentionDays: [1, 90],
  providerRequestRetentionMiB: [64, 16_384],
  originIpRetentionDays: [1, 365],
  streamSilenceLimitMs: [35_000, 300_000],
  streamReviveSilenceLimitMs: [35_000, 300_000],
  toastDurationMs: [2_000, 15_000],
};
